/**
 * useAnalytics — Top-level GA4 instrumentation hook.
 *
 * Mount this once inside the router so it can:
 *   1. Initialize GA4 (disable gtag auto page_view for SPA usage).
 *   2. Fire `page_view` + route-specific view events on every navigation.
 *   3. Fire GA4's recommended `login` event whenever a player becomes signed in (or
 *      switches account) — including a session restored silently on load.
 *
 * Other funnel events (begin_checkout, purchase, first_pet_action, etc.)
 * live in the components that own their business action.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAccountId } from "../auth/useAccountId";
import { ANALYTICS_CONSENT_EVENT, type AnalyticsConsent } from "../lib/analyticsConsent";
import {
  applyAnalyticsConsent,
  initializeAnalytics,
  initializePostHogAnalytics,
  trackEvent,
  trackPageView,
  type PageType,
} from "../lib/analytics";

/** Fallback GA4 measurement ID mirrors the one hardcoded in index.html. */
const DEFAULT_GA4_MEASUREMENT_ID = "G-K09WY68Q0B";

/** GA4's recommended sign-in event, and the one method web2 signs in with. */
const LOGIN_EVENT = "login";
const LOGIN_METHOD = "supabase";

/**
 * Map a React Router pathname to a high-level page_type. Kept in sync
 * with the app's routing in `App.tsx`.
 */
function derivePageType(pathname: string): PageType {
  if (pathname === "/" || pathname === "") return "home";
  if (pathname === "/adopt") return "adopt";
  if (pathname === "/play" || pathname.startsWith("/play/")) return "play";
  if (pathname === "/rankings") return "rankings";
  if (pathname === "/trainers") return "trainers";
  if (pathname === "/help") return "help";
  if (pathname === "/pvp-lab") return "pvp_lab";
  if (pathname === "/pvp-trial") return "pvp_trial";
  if (pathname === "/pvp-ranked") return "pvp_ranked";
  if (pathname === "/battle") return "battle";
  if (pathname === "/whitepaper") return "whitepaper";
  if (pathname === "/terms") return "terms";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/media") return "media";
  if (pathname === "/news") return "news";
  if (pathname.startsWith("/suikamon/") || pathname.startsWith("/pet/")) return "pet_detail";
  if (pathname.startsWith("/trainer/")) return "trainer_profile";
  // Single-segment paths route to SpeciesPage — tagged from inside that
  // component (once species resolution succeeds) rather than here.
  return "other";
}

export function useAnalytics(): void {
  const location = useLocation();
  const { accountId } = useAccountId();
  const previousAccountRef = useRef<string | null>(null);
  const lastTrackedPathRef = useRef<string | null>(null);

  // Initialize once — must happen before any events fire.
  useEffect(() => {
    const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID || DEFAULT_GA4_MEASUREMENT_ID;
    initializeAnalytics(measurementId);
    initializePostHogAnalytics(
      import.meta.env.VITE_POSTHOG_KEY,
      import.meta.env.VITE_POSTHOG_HOST,
    );
  }, []);

  // The consent bar (or "Cookie settings") changed the player's choice → apply it to both tools.
  useEffect(() => {
    const onConsent = (event: Event) => {
      applyAnalyticsConsent((event as CustomEvent<AnalyticsConsent | null>).detail);
    };
    window.addEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
  }, []);

  // Fire page_view on every navigation. We skip species detail routes
  // (single-segment paths that map to SpeciesPage) because the species
  // component fires `species_page_view` itself once the name is validated.
  useEffect(() => {
    const pathname = location.pathname;
    if (lastTrackedPathRef.current === pathname) return;

    const pageType = derivePageType(pathname);

    // Single-segment paths (e.g. /bireon) are handled by SpeciesPage,
    // which fires both `page_view` and `species_page_view` with species
    // context. Skip the generic fire-and-forget here to avoid duplicates.
    const isSpeciesDetailCandidate =
      pageType === "other" &&
      pathname.length > 1 &&
      pathname.startsWith("/") &&
      !pathname.slice(1).includes("/");
    if (isSpeciesDetailCandidate) {
      lastTrackedPathRef.current = pathname;
      return;
    }

    trackPageView({ path: pathname, pageType });
    lastTrackedPathRef.current = pathname;
  }, [location.pathname]);

  // Fire `login` on every transition from signed-out → signed-in (or an account switch).
  useEffect(() => {
    const previous = previousAccountRef.current;

    if (accountId && accountId !== previous) {
      trackEvent(LOGIN_EVENT, { method: LOGIN_METHOD, page_path: location.pathname });
    }

    previousAccountRef.current = accountId;
    // location.pathname intentionally excluded — we don't want to re-fire
    // this on every navigation, only on account change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);
}
